import React, {
  FunctionComponent,
  useContext,
  useState,
  useEffect,
} from "react";
import { Button, Card } from "@cmsgov/ds-medicare-gov";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { v1 as uuidv1 } from "uuid";
import { Routes } from "../../app";
import { AppStore } from "../../app/store";
import { ActionType } from "../../@types";
import {
  displayPackage,
  displayDrug,
  sendDrugConfirmationTealiumEvent,
  hasDuplicatePrescriptions,
} from "../../helpers/prescriptionHelpers";
import { useTranslate, CONSTANTS, sendTealiumEvent } from "../../helpers";
import { DuplicatePrescriptionNotification } from "../../components/DuplicatePrescriptionNotification";
import { BeneNewPlanSearchLink } from "../../components/BeneNewPlanSearchLink";
import {
  clearMbpReferrerUri,
  mbpReferrerUriClickHandler,
} from "../../helpers/mbpReferrerHelpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import { createGa4Event, Ga4Event } from "../../helpers/analytics";

export const prescriptionCardTestIdName = "ViewPrescriptions__prescriptionCard";

interface ViewPrescriptionsProps {
  onEdit: (drug: {
    is_generic: boolean;
    rxcui: string;
    index: number;
    name: string;
  }) => void;
  addMoreDrugs: () => void;
  goToPharmacySelection: () => void;
  resetPrescriptionIndex: () => void;
}

const ViewPrescriptions: FunctionComponent<ViewPrescriptionsProps> = props => {
  const t = useTranslate();
  const {
    state,
    state: { beneficiary, prescriptions, mbpReferrerUri },
    dispatch,
  } = useContext(AppStore.AppContext);
  const flags = useFlags();
  const [toastId, setToastId] = useState("");
  const TOAST_DURATION = 5000;
  const isStandaloneDrugPage = mbpReferrerUri !== "";

  useEffect(() => {
    setToastId(uuidv1());
  }, []);

  let footer = (
    <Button
      variation="primary"
      onClick={(): void => {
        sendDrugConfirmationTealiumEvent(prescriptions);
        props.goToPharmacySelection();
      }}
      className="e2e-drug-next ds-u-margin-top--2 ds-u-md-margin-top--0 mct-u-width--full mct-u-md-width--auto"
    >
      {t("prescription.button.done_adding_drugs")}
    </Button>
  );

  const standaloneFooter = (
    <>
      <a
        href={mbpReferrerUri}
        onClick={e => {
          sendTealiumEvent("My Saved Drugs", "Drugs managed");
          createGa4Event({
            event_name: Ga4Event.PLAN_FINDER_SAVED_DRUGS_MANAGED,
            drug_count: prescriptions.length,
            text: "OK",
            state,
            flags,
          });
          mbpReferrerUriClickHandler({ dispatch, mbpReferrerUri, e });
        }}
        className="ds-c-button ds-c-button--primary e2e-drug-next ds-u-margin-top--2 ds-u-md-margin-top--0 ds-u-md-margin-right--2 mct-u-width--full mct-u-md-width--auto"
      >
        {t("ok")}
      </a>
      <BeneNewPlanSearchLink
        beforeRoutingCallback={() => {
          sendTealiumEvent(
            "Find Plans Landing Page - Login",
            "Start a New Plan Search"
          );
          createGa4Event({
            event_name: Ga4Event.PLAN_FINDER_STARTED,
            text: "Start a New Plan Search",
            flags,
            state,
          });
          clearMbpReferrerUri(dispatch);
        }}
        className="ds-c-button ds-c-button--primary-alt e2e-drug-next ds-u-margin-top--2 ds-u-md-margin-top--0 mct-u-width--full mct-u-md-width--auto"
        messageId="manage_prescriptions.start_new_plan_search"
      />
    </>
  );

  if (isStandaloneDrugPage) {
    footer = standaloneFooter;
  }

  return (
    <div className="ViewPrescriptions">
      <h2 className="ds-u-visibility--screen-reader">
        {t("prescription.drug_list")}
      </h2>

      <>
        {beneficiary && prescriptions.length > 0 && (
          <p className="ds-u-measure--wide ds-u-margin-bottom--3 ds-u-margin-top--0">
            {t("view_prescriptions.drug_list_disclaimer")}
          </p>
        )}

        {prescriptions.length === 0 && isStandaloneDrugPage && (
          <p className="ds-u-margin-top--0">
            {t("manage_prescriptions.no_drugs")}
          </p>
        )}

        {prescriptions.length > 0 && (
          <div className="ViewPrescriptions__listCardsContainer">
            {hasDuplicatePrescriptions(prescriptions) && (
              <DuplicatePrescriptionNotification className="ds-u-margin-bottom--2" />
            )}
            <ol className="ViewPrescriptions__listCards ds-c-list--bare ds-u-padding--0">
              {prescriptions.map((prescription, index) => {
                return (
                  <li key={index} data-testid={prescriptionCardTestIdName}>
                    <Card
                      className={`${prescriptionCardTestIdName} ds-l-container`}
                    >
                      <div className="ds-l-row">
                        <div className="ds-l-col--12 ds-l-sm-col--4">
                          <div>
                            <h3 className="ViewPrescriptions__prescriptionName e2e-drug-list-name">
                              {displayDrug(t, prescription)}
                            </h3>

                            {prescription.is_generic && (
                              <p className="ViewPrescriptions__prescriptionCardText">
                                {t("prescription.generic")}
                              </p>
                            )}
                          </div>
                        </div>

                        <div
                          className={`ds-l-col--12 ds-l-sm-col--3${
                            prescription.use_package_quantity
                              ? " ds-u-margin-top--2 ds-u-sm-margin-top--0"
                              : ""
                          }`}
                        >
                          {prescription.use_package_quantity && (
                            <>
                              {" "}
                              <h4 className="ViewPrescriptions__prescriptionCardLabel">
                                {t("prescription.package_type")}
                              </h4>
                              <p className="ViewPrescriptions__prescriptionCardText">
                                {displayPackage(t, prescription)}
                              </p>
                            </>
                          )}
                        </div>

                        <div className="ds-l-col--12 ds-l-sm-col--2 ds-u-margin-top--2 ds-u-sm-margin-top--0">
                          <h4 className="ViewPrescriptions__prescriptionCardLabel">
                            {t("prescription.quantity")}
                          </h4>

                          <p className="ViewPrescriptions__prescriptionCardText">
                            {prescription.quantity}
                          </p>
                        </div>

                        <div className="ds-l-col--12 ds-l-sm-col--3 ds-u-margin-top--2 ds-u-sm-margin-top--0">
                          <h4 className="ViewPrescriptions__prescriptionCardLabel">
                            {t("prescription.frequency")}
                          </h4>

                          <p className="ViewPrescriptions__prescriptionCardText">
                            {t(
                              CONSTANTS.prescriptionFrequencyLabels[
                                prescription.frequency
                              ]
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="ds-l-row">
                        <div className="ds-l-col ds-u-display--flex mct-hide-print">
                          <Button
                            variation="transparent"
                            className="ViewPrescriptions__linkButton mct-hide-print"
                            aria-label={`${t("remove")} ${displayDrug(
                              t,
                              prescription
                            )}`}
                            onClick={(): void => {
                              const drugType = prescription.is_generic
                                ? "generic"
                                : "brand";
                              if (isStandaloneDrugPage) {
                                sendTealiumEvent(
                                  "My Saved Drugs",
                                  "drug removed"
                                );
                                createGa4Event({
                                  event_name: Ga4Event.DRUG_REMOVED,
                                  drug_name: prescription.name,
                                  drug_type: drugType,
                                  text: "Remove drug",
                                  state,
                                  flags,
                                });
                              } else {
                                createGa4Event({
                                  event_name: Ga4Event.DRUG_REMOVED,
                                  drug_type: drugType,
                                  state,
                                  flags,
                                });
                              }

                              dispatch({
                                type: ActionType.DELETE_PRESCRIPTION,
                                payload: { index },
                              });
                              props.resetPrescriptionIndex();

                              if (!toast.isActive(toastId)) {
                                toast(
                                  <div className="ViewPrescriptions__toast">
                                    <strong>{t("prescription.success")}</strong>
                                    <p className="ds-u-margin--0">
                                      {t(
                                        "prescription.successfully_removed_drug"
                                      )}
                                    </p>
                                  </div>,
                                  {
                                    toastId,
                                    containerId: "globalToastContainer",
                                    autoClose: TOAST_DURATION,
                                    className: "ds-u-padding--0",
                                    position: toast.POSITION.TOP_CENTER,
                                    draggable: false,
                                    hideProgressBar: true,
                                    closeButton: false,
                                  }
                                );
                              }
                            }}
                          >
                            {t("prescription.remove_drug")}
                          </Button>
                        </div>
                        <div className="ds-l-col ds-l-md-col--3 ds-u-display--flex ds-u-justify-content--end ds-u-md-justify-content--start mct-hide-print">
                          <Button
                            variation="transparent"
                            className="ViewPrescriptions__linkButton ds-u-text-align--center ds-u-md-text-align--left ds-u-lg-text-align--right mct-hide-print"
                            aria-label={`${t("edit")} ${displayDrug(
                              t,
                              prescription
                            )}`}
                            onClick={(): void => {
                              if (isStandaloneDrugPage) {
                                sendTealiumEvent("My Saved Drugs", "edit drug");
                                createGa4Event({
                                  event_name: Ga4Event.PLAN_FINDER_EDIT_DRUG,
                                  drug_name: prescription.name,
                                  drug_type: prescription.is_generic
                                    ? "generic"
                                    : "brand",
                                  text: "Edit drug",
                                  state,
                                  flags,
                                });
                              } else {
                                // istanbul ignore next: tested elsewhere
                                sendTealiumEvent(
                                  "Find Plans - Add Drugs",
                                  "Edit Drug List"
                                );
                              }
                              props.onEdit({
                                is_generic: prescription.is_generic,
                                rxcui: prescription.rxcui,
                                index,
                                name: prescription.name,
                              });
                            }}
                          >
                            {t("prescription.edit_drug")}
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        <div className="ds-u-margin-top--5 mct-u-width--full ds-u-display--flex ds-u-flex-wrap--wrap">
          {beneficiary && beneficiary.pdes && beneficiary.pdes.length > 0 && (
            <Link
              to={Routes.pdePage}
              className="ds-c-button ds-u-margin-right--0 ds-u-md-margin-right--2 ds-u-margin-bottom--2 ds-u-md-margin-bottom--0 mct-u-width--full mct-u-md-width--auto e2e-add-recently-filled-drugs"
              onClick={(): void => {
                if (isStandaloneDrugPage) {
                  sendTealiumEvent(
                    "My Saved Drugs",
                    "Add Recently Filled Drugs"
                  );
                  createGa4Event({
                    event_name: Ga4Event.PLAN_FINDER_ADD_RECENT_FILLED_DRUGS,
                    text: "Add recently filled drugs",
                    state,
                    flags,
                  });
                } else {
                  sendTealiumEvent(
                    "Find Plans - Add Drugs",
                    "Add Recently Filled Drugs"
                  );
                }
              }}
            >
              {t("view_prescriptions.add_recently_filled_drugs")}
            </Link>
          )}
          <Button
            onClick={(): void => {
              if (isStandaloneDrugPage) {
                const text =
                  prescriptions.length === 0
                    ? "Add a Drug"
                    : "Add Another Drug";
                sendTealiumEvent("My Saved Drugs", text);
                createGa4Event({
                  event_name: Ga4Event.VIEW_DRUG_LIST,
                  text,
                  state,
                  flags,
                });
              } else {
                // istanbul ignore next: tested elsewhere
                sendTealiumEvent("Find Plans - Add Drugs", "Add Another Drug");
              }
              props.addMoreDrugs();
            }}
            className="e2e-drug-add-more mct-u-width--full mct-u-md-width--auto mct-hide-print"
          >
            {prescriptions.length === 0 && isStandaloneDrugPage
              ? t("manage_prescriptions.add_a_drug")
              : t("view_prescriptions.find_and_add_drug")}
          </Button>
        </div>
      </>

      <div className="ViewPrescriptions__footer mct-hide-print ds-u-display--flex ds-u-flex-wrap--wrap">
        {footer}
      </div>
    </div>
  );
};

export default ViewPrescriptions;
